import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import layout from './styles.module.scss'
import {
  formatDisplayValue,
  formatIdLabel,
  getAddressEntries,
  getVisibleEntries,
  groupEntriesIntoRows,
  humanizeKey,
  isPlainObject,
} from './helpers'
import { DEFAULT_FORM_THEME, FORM_THEMES, getFormTheme, resolveFormTheme } from './themes'

const ThemeCtx = createContext({
  t: name => layout[name] || '',
  config: FORM_THEMES[DEFAULT_FORM_THEME],
})

const useT = () => useContext(ThemeCtx).t

const FieldLabel = ({ label, required }) => {
  const t = useT()
  return (
    <label className={t('label')}>
      {label}
      {required ? <span className={t('required')}> *</span> : null}
    </label>
  )
}

const TextControl = ({ value }) => {
  const t = useT()
  return <input className={t('input')} type='text' value={formatDisplayValue(value)} readOnly tabIndex={-1} />
}

const DateControl = ({ value }) => {
  const t = useT()
  return (
    <div className={t('inputWithIcon')}>
      <input className={t('input')} type='text' value={formatDisplayValue(value)} readOnly tabIndex={-1} />
      <span className={t('calendarIcon')} aria-hidden='true' />
    </div>
  )
}

const SelectControl = ({ value }) => {
  const t = useT()
  return (
    <div className={t('inputWithIcon')}>
      <input className={t('input')} type='text' value={formatDisplayValue(value)} readOnly tabIndex={-1} />
      <span className={t('chevronIcon')} aria-hidden='true' />
    </div>
  )
}

const TextareaControl = ({ value }) => {
  const t = useT()
  return <textarea className={t('textarea')} value={formatDisplayValue(value)} readOnly tabIndex={-1} rows={4} />
}

const BooleanControl = ({ value }) => {
  const t = useT()
  return (
    <div className={t('radioGroup')} role='radiogroup'>
      {['Yes', 'No'].map(option => {
        const selected = (value === true && option === 'Yes') || (value === false && option === 'No')
        return (
          <label key={option} className={t('radioItem')}>
            <span className={`${t('radio')} ${selected ? t('radioSelected') : ''}`} />
            <span>{option}</span>
          </label>
        )
      })}
    </div>
  )
}

const CheckboxGroupControl = ({ value }) => {
  const t = useT()
  const items = Array.isArray(value) ? value : []
  return (
    <div className={t('checkboxGroup')}>
      {items.map(item => (
        <label key={item} className={t('checkboxItem')}>
          <span className={`${t('checkbox')} ${t('checkboxChecked')}`} />
          <span>{formatIdLabel(item)}</span>
        </label>
      ))}
    </div>
  )
}

const ListControl = ({ value }) => {
  const t = useT()
  return (
    <div className={t('checkboxGroup')}>
      {(value || []).map((item, index) => (
        <div key={`${item}-${index}`} className={t('listItem')}>
          {formatDisplayValue(item)}
        </div>
      ))}
    </div>
  )
}

const FieldControl = ({ field }) => {
  switch (field.type) {
    case 'boolean':
      return <BooleanControl value={field.value} />
    case 'checkboxGroup':
      return <CheckboxGroupControl value={field.value} />
    case 'list':
      return <ListControl value={field.value} />
    case 'date':
      return <DateControl value={field.value} />
    case 'select':
      return <SelectControl value={field.value} />
    case 'textarea':
      return <TextareaControl value={field.value} />
    default:
      return <TextControl value={field.value} />
  }
}

const FieldBlock = ({ field, fullWidth }) => {
  const t = useT()
  return (
    <div className={`${t('field')} ${fullWidth ? t('fieldFull') : ''}`}>
      <FieldLabel label={field.label} required={field.required} />
      <FieldControl field={field} />
    </div>
  )
}

const AddressBlock = ({ field }) => {
  const t = useT()
  const entries = getAddressEntries(field.value)
  const rows = []
  for (let i = 0; i < entries.length; i += 2) {
    rows.push(entries.slice(i, i + 2))
  }

  return (
    <div className={t('block')}>
      <div className={t('subheading')}>{field.label}</div>
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className={t('row')}>
          {row.map(([key, value]) => (
            <div key={key} className={t('field')}>
              <FieldLabel label={humanizeKey(key)} />
              <TextControl value={value} />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

const RepeatingBlock = ({ field }) => {
  const t = useT()
  const items = Array.isArray(field.value) ? field.value : []

  return (
    <div className={t('block')}>
      <div className={t('subheading')}>{field.label}</div>
      {items.map((item, index) => {
        const entries = getVisibleEntries(item).filter(([key]) => key !== 'Id' && key !== 'ItemNumber')
        const rows = groupEntriesIntoRows(entries, item)
        return (
          <div key={item.Id || index} className={t('repeatCard')}>
            <div className={t('repeatCardTitle')}>Field {index + 1}</div>
            {rows.map((row, rowIndex) => (
              <ObjectRow key={rowIndex} row={row} />
            ))}
          </div>
        )
      })}
    </div>
  )
}

const GroupBlock = ({ field }) => {
  const t = useT()
  const entries = getVisibleEntries(field.value)
  const rows = groupEntriesIntoRows(entries, field.value)

  return (
    <div className={t('block')}>
      <div className={t('subheading')}>{field.label}</div>
      {rows.map((row, rowIndex) => (
        <ObjectRow key={rowIndex} row={row} />
      ))}
    </div>
  )
}

const ObjectRow = ({ row }) => {
  const t = useT()
  if (row.kind === 'fields') {
    return (
      <div className={t('row')}>
        {row.fields.map(field => (
          <FieldBlock key={field.key} field={field} fullWidth={row.fields.length === 1} />
        ))}
      </div>
    )
  }

  const { field } = row
  if (field.type === 'address') return <AddressBlock field={field} />
  if (field.type === 'repeating') return <RepeatingBlock field={field} />
  if (field.type === 'group') return <GroupBlock field={field} />

  return <FieldBlock field={field} fullWidth />
}

const ObjectFields = ({ data }) => {
  const entries = getVisibleEntries(data)
  const rows = groupEntriesIntoRows(entries, data)

  return (
    <>
      {rows.map((row, index) => (
        <ObjectRow key={index} row={row} />
      ))}
    </>
  )
}

const SectionBody = ({ value, keyName }) => {
  if (Array.isArray(value)) {
    return (
      <ObjectRow
        row={{
          kind: 'block',
          field: {
            key: keyName,
            value,
            type: value.every(item => isPlainObject(item)) ? 'repeating' : 'checkboxGroup',
            label: humanizeKey(keyName),
            required: false,
          },
        }}
      />
    )
  }

  return <ObjectFields data={value} />
}

export const CognitoFormViewer = ({ data, title }) => {
  const themeSlug = resolveFormTheme()
  const config = getFormTheme(themeSlug)
  const t = useMemo(
    () => name => [layout[name], config.styles?.[name]].filter(Boolean).join(' '),
    [config]
  )
  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    setActiveTab(0)
  }, [data])

  const ctx = useMemo(() => ({ t, config }), [t, config])

  if (!isPlainObject(data) || !Object.keys(data).length) {
    return <div className={t('empty')}>No form data available</div>
  }

  const sections = getVisibleEntries(data).filter(([, value]) => isPlainObject(value) || Array.isArray(value))
  const topLevelScalars = getVisibleEntries(data).filter(([, value]) => !isPlainObject(value) && !Array.isArray(value))
  const hasSections = sections.length > 0
  const visibleSections = config.tabs && hasSections ? [sections[Math.min(activeTab, sections.length - 1)]] : sections

  return (
    <ThemeCtx.Provider value={ctx}>
      <div className={t('viewer')} data-theme={themeSlug}>
        <div className={t('sheet')}>
          {config.brand ? (
            <div className={t('brand')}>
              <span className={t('brandMark')} aria-hidden='true' />
              <div className={t('brandName')}>{config.brand}</div>
            </div>
          ) : null}

          {title ? <h1 className={t('title')}>{title}</h1> : null}

          {config.tabs && hasSections ? (
            <nav className={t('tabs')} aria-label='Form sections'>
              {sections.map(([key], index) => (
                <button
                  key={key}
                  type='button'
                  className={`${t('tab')} ${index === activeTab ? t('tabActive') : ''}`}
                  onClick={() => setActiveTab(index)}
                >
                  {humanizeKey(key)}
                </button>
              ))}
            </nav>
          ) : null}

          {hasSections ? (
            <>
              {topLevelScalars.length > 0 && !config.tabs && (
                <section className={t('section')}>
                  <ObjectFields data={Object.fromEntries(topLevelScalars)} />
                </section>
              )}
              {visibleSections.map(([key, value], index) => {
                const sectionIndex = config.tabs ? activeTab : index
                return (
                  <section key={key} className={t('section')}>
                    <h2 className={t('sectionTitle')}>
                      {config.numberedSections ? `${sectionIndex + 1}. ` : ''}
                      {humanizeKey(key)}
                    </h2>
                    <SectionBody value={value} keyName={key} />
                  </section>
                )
              })}
            </>
          ) : (
            <section className={t('section')}>
              <ObjectFields data={data} />
            </section>
          )}
        </div>
      </div>
    </ThemeCtx.Provider>
  )
}
